/**
 * web/src/screens/Notebook/filters.tsx — the links view's one row: search,
 * the Filters button with its live count and panel, the "N of M" counter and
 * the List/Grid switch (card vncVrGk1hc9i: moved out of index.tsx unchanged).
 */
import type { ReactNode } from 'react';
import { notebookRouteFor } from '../../../../shared/types';
import { titleCase } from '../../lib/titleCase';
import { type NotebookState } from './hooks';

export function LinksBar({ s, viewToggle }: { s: NotebookState; viewToggle: ReactNode | null }) {
  const {
    linkTab, collection, linkItems, search, setSearch, filtersOpen, setFiltersOpen,
    activeFilterCount, chipLinks, kindChips, subtypeTabs, subtypeFilter, setSubtypeFilter,
    kindFilter, setKindFilter, statusFilter, setStatusFilter, clearFilters,
    visible, counterBase,
  } = s;
  return (
    <>
        {/* ── The links view (card LcJbm9wsWa0B, PRD §5/§6.7) ────────────────
            Founder email 2026-10-19: the filter stack was busy — chips, tabs,
            pills and search over four rows. Sol review 2026-10-19 (card
            xNGOA5rZz9LV) went further: the cards, not the controls, should own
            the first screen. So the default visit is now ONE row — search, a
            Filters button carrying the live count of what is narrowing, and
            the "N of M" the narrowings leave. Card vHyQrgOMJyPg finished the
            job the founder asked for on 2026-10-22: the chip row is gone
            entirely, and Filters carries ONE category dropdown (every kind,
            counts in the option labels) alongside subtype and lifecycle — so
            there is one search and one filter door, not a row of pills. */}
        {/* Card AieaJRs6sLga: the bar normally hides itself when the tab holds
            no links, but a SERVER search that matches nothing also empties
            `linkItems` — hiding the row then takes the search field away with
            it, so the query could never be cleared. A live query keeps the row
            mounted regardless of the match count. */}
        {linkTab && (linkItems.length > 0 || search.trim().length > 0) && (
          <div className="nb-links-view" data-test="nb-links-view">
            <div className="nb-links-bar">
              <input
                type="search"
                data-test="nb-search"
                className="nb-search"
                /* Card xUZFWf8JtHo2 item 3 — the placeholder names what this tab
                   actually holds. It used to read "Search your links" on
                   Everything too, and the query never touched typed cards. */
                placeholder={linkTab && collection === 'link' ? 'Search your links' : 'Search the notebook'}
                aria-label={linkTab && collection === 'link' ? 'Search your links' : 'Search the notebook'}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <button
                type="button"
                data-test="nb-filters-toggle"
                className="nb-filters-btn"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen(o => !o)}
              >
                Filters
                {activeFilterCount > 0 && (
                  <span className="nb-filters-n" data-test="nb-filters-count">{activeFilterCount}</span>
                )}
              </button>
              {/* Card xUZFWf8JtHo2 item 1 — the numerator is the rendered count
                  and the base follows the tab, so it can never read "1 of 1"
                  above several cards. */}
              <span className="nb-result-count" data-test="nb-result-count">
                {visible.length} of {counterBase}
              </span>
              {/* Card 2wn9seit8zGi (PRD F5) — List / Grid rides the SAME row as
                  the count instead of claiming a row of its own: on a 900px
                  desktop viewport every chrome row above the list costs a card
                  of density, and the PRD's whole point is more cards on
                  screen. Desktop-only — grid is a use-of-width move and there
                  is no width to use on a phone (PRD L5). */}
              {viewToggle}
            </div>

            {filtersOpen && (
              <div className="nb-links-tools" data-test="nb-filters-panel">
                {/* Card vHyQrgOMJyPg — the row of kind chips is gone: one
                    search box, one Filters button, and ONE dropdown that
                    carries every category with its count. The count in the
                    option label is the narrowed one (same numbers the chips
                    showed), so a category with nothing matching reads an
                    honest 0 instead of disappearing. */}
                <select
                  className="nb-filter-select"
                  data-test="nb-kind-select"
                  aria-label="Category"
                  value={kindFilter}
                  onChange={e => { setKindFilter(e.target.value); setSubtypeFilter('all'); }}
                >
                  <option value="all">All categories ({chipLinks.length})</option>
                  {kindChips.map(c => (
                    <option key={c.kind} value={c.kind}>
                      {notebookRouteFor(c.kind).kindLabel} ({c.n})
                    </option>
                  ))}
                </select>
                {subtypeTabs.length > 0 && (
                  <select
                    className="nb-filter-select"
                    data-test="nb-subtype-select"
                    aria-label="Subtype"
                    value={subtypeFilter}
                    onChange={e => setSubtypeFilter(e.target.value)}
                  >
                    <option value="all">All subtypes</option>
                    {subtypeTabs.map(s => (
                      <option key={s} value={s}>{titleCase(s)}</option>
                    ))}
                  </select>
                )}
                <select
                  className="nb-filter-select"
                  data-test="nb-status-select"
                  aria-label="Saved, planned or done"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
                >
                  <option value="all">Everything</option>
                  <option value="saved">Saved</option>
                  <option value="planned">Planned</option>
                  <option value="done">Done</option>
                </select>
                <button
                  type="button"
                  data-test="nb-filters-clear"
                  className="nb-filters-clear"
                  disabled={activeFilterCount === 0}
                  onClick={clearFilters}
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
    </>
  );
}
